import React, { forwardRef, useImperativeHandle } from 'react';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';

import DataTableModule from '@/__core/layout/module/DataTableModule';
import { sharedListStyles } from '@/__core/styles/sharedListStyles';

import { InventoryStocktakeRow } from '../types';

export type StocktakeListHandle = {
  requestRefresh: () => void;
};

interface StocktakeListProps {
  rows: InventoryStocktakeRow[];
  loading?: boolean;
  onSelectRow?: (row: InventoryStocktakeRow) => void;
  onRefresh?: () => void;
}

const statusColor = (status: string): 'default' | 'warning' | 'success' | 'info' | 'error' => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'draft':
      return 'warning';
    case 'posted':
      return 'info';
    default:
      return 'default';
  }
};

const StocktakeList = forwardRef<StocktakeListHandle, StocktakeListProps>(function StocktakeList(
  { rows, loading = false, onSelectRow, onRefresh },
  ref
) {
  useImperativeHandle(
    ref,
    () => ({
      requestRefresh: () => onRefresh?.(),
    }),
    [onRefresh]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 1,
          height: 220,
        }}
      >
        <Typography variant='body1' color='text.secondary'>
          Không có phiếu kiểm kê
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Thử thay đổi bộ lọc hoặc tạo phiếu mới.
        </Typography>
      </Box>
    );
  }

  const columns: Array<{
    id: string;
    label: string;
    width?: string;
    align?: 'left' | 'center' | 'right';
    render?: (_: unknown, row: InventoryStocktakeRow) => React.ReactNode;
  }> = [
    {
      id: 'status',
      label: 'Trạng thái',
      width: '140px',
      render: (_: unknown, row: InventoryStocktakeRow) => (
        <Chip size='small' color={statusColor(row.status)} label={row.status} />
      ),
    },
    {
      id: 'code',
      label: 'Mã kiểm kê',
      width: '160px',
      render: (_: unknown, row: InventoryStocktakeRow) => <Typography sx={{ fontWeight: 600 }}>{row.code}</Typography>,
    },
    { id: 'name', label: 'Tên phiếu kiểm kê', width: '260px' },
    { id: 'store_name', label: 'Kho', width: '200px' },
    { id: 'date', label: 'Ngày', width: '140px' },
    {
      id: 'note',
      label: 'Ghi chú',
      width: '240px',
      render: (_: unknown, row: InventoryStocktakeRow) => row.note || '-',
    },
    { id: 'company_name', label: 'Công ty', width: '220px' },
  ];

  return (
    <Box sx={sharedListStyles.container}>
      <DataTableModule
        data={rows}
        columns={columns}
        leftFilterWidth={0}
        onRowSelect={() => undefined}
        onSelectAll={() => undefined}
        onRowClick={(row) => onSelectRow?.(row as InventoryStocktakeRow)}
        getRowId={(row) => (row as InventoryStocktakeRow).id}
        isRowSelected={() => false}
        areAllRowsSelected={false}
        areSomeRowsSelected={false}
        showCheckbox={false}
      />
    </Box>
  );
});

export default StocktakeList;
